import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FileText, Tag, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { documentService } from '@/services'
import { useAppStore } from '@/stores/appStore'
import type { Document as ApiDocument } from '@/types/api'

/** Category label map for human-friendly display. */
const CATEGORY_LABELS: Record<string, string> = {
  fundraising_finance: 'Fundraising & Finance',
  customer_intelligence: 'Customer Intelligence',
  product_engineering: 'Product & Engineering',
  go_to_market: 'Go-to-Market',
  legal_compliance: 'Legal & Compliance',
  team_ops: 'Team & Ops',
  unsorted: 'Unsorted',
}

/** Human-readable section labels for structured context. */
const SECTION_LABELS: Record<string, string> = {
  mission: 'Mission & Vision',
  targetMarket: 'Target Market',
  businessModel: 'Business Model',
  product: 'Product',
  goToMarket: 'Go-to-Market',
  competition: 'Competition',
  team: 'Team',
  fundraising: 'Fundraising',
}

interface StructuredField {
  label: string
  value: string | string[] | null
}

/** Determine whether a structured context section has data. */
function getSectionState(
  section: Record<string, unknown> | undefined
): 'empty' | 'partial' | 'full' {
  if (!section) return 'empty'
  const values = Object.values(section)
  const hasData = values.some(
    (v) => v !== null && v !== undefined && v !== ''
  )
  if (!hasData) return 'empty'
  const filled = values.filter(
    (v) => v !== null && v !== undefined && v !== ''
  ).length
  return filled === values.length ? 'full' : 'partial'
}

/** Extract fields from a structured context section. */
function getSectionFields(
  section: Record<string, unknown> | undefined
): StructuredField[] {
  if (!section) return []
  return Object.entries(section)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({
      label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase()),
      value: Array.isArray(value) ? value : value ?? null,
    })) as StructuredField[]
}

/** Check if all structured context sections are empty. */
function allSectionsEmpty(
  structured: Record<string, unknown> | undefined
): boolean {
  if (!structured) return true
  return Object.values(SECTION_LABELS).every((_, i) => {
    const key = Object.keys(SECTION_LABELS)[i]
    const section = structured[key] as Record<string, unknown> | undefined
    return getSectionState(section) === 'empty'
  })
}

export function DocumentDetailPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const startupId = useAppStore((s) => s.startup?.id)
  const [doc, setDoc] = useState<ApiDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!documentId || !startupId) return
    setLoading(true)
    setError(null)
    documentService
      .get(startupId, documentId)
      .then((d) => setDoc(d))
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to load document')
      )
      .finally(() => setLoading(false))
  }, [startupId, documentId])

  const extracted = doc?.extractedData
  const category = extracted?.category
  const categoryLabel = category ? CATEGORY_LABELS[category] ?? category : null
  const confidence = extracted?.confidence
  const summary = extracted?.summary
  const topics = extracted?.topics
  const structured = extracted?.structuredContext as Record<string, unknown> | undefined
  const hasAnyData =
    !!summary ||
    (topics && topics.length > 0) ||
    (structured && !allSectionsEmpty(structured))

  return (
    <div className="relative flex h-full flex-col bg-bg-primary">
      {/* Header */}
      <div className="shrink-0 border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/documents')}
            className="gap-1.5 text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <FileText className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-lg font-semibold text-text-primary truncate">
              {doc?.name?.trim() || 'Document'}
            </h1>
            <p className="text-xs text-text-tertiary">
              {doc?.processedAt
                ? `Processed ${new Date(
                    doc.processedAt
                  ).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}`
                : 'Processing…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {categoryLabel && (
            <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
              <Tag className="h-3 w-3" />
              {categoryLabel}
            </span>
          )}
          {confidence != null && (
            <span className="text-xs text-text-tertiary">
              {Math.round(confidence * 100)}% confidence
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-3 text-text-tertiary" />
            <p className="text-sm text-text-secondary">{error}</p>
            <button
              onClick={() => navigate('/documents')}
              className="mt-3 text-xs text-primary hover:underline"
            >
              Return to documents
            </button>
          </div>
        </div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">
            {/* Summary */}
            {summary && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-3">
                  Executive Summary
                </h2>
                <div className="rounded-lg border border-border bg-bg-elevated p-5">
                  <p className="text-sm leading-relaxed text-text-primary">
                    {summary}
                  </p>
                </div>
              </section>
            )}

            {/* Topics */}
            {topics && topics.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-3">
                  Topics
                </h2>
                <div className="flex flex-wrap gap-2">
                  {topics.map((topic) => (
                    <span
                      key={topic}
                      className="text-xs px-2.5 py-1 rounded-full bg-bg-surface text-text-secondary border border-border"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Structured Context */}
            {structured && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-3">
                  Structured Context
                </h2>
                <div className="space-y-2">
                  {Object.entries(SECTION_LABELS)
                    .map(([key, label]) => {
                      const section = structured?.[key as keyof typeof SECTION_LABELS] as Record<string, unknown> | undefined
                      const state = getSectionState(section)
                      const fields = getSectionFields(section)
                      // Skip sections with no non-empty fields
                      if (fields.length === 0) return null
                      return (
                        <StructuredSection
                          key={key}
                          label={label}
                          state={state}
                          fields={fields}
                        />
                      )
                    })
                    .filter(Boolean)}
                </div>
              </section>
            )}

            {/* No data state */}
            {!hasAnyData && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <FileText className="mb-3 h-8 w-8 text-text-tertiary" />
                <p className="text-sm font-medium text-text-primary">
                  No data available yet
                </p>
                <p className="mt-1 text-xs text-text-tertiary">
                  The document may still be processing.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}

/* ── Structured Section ─────────────────────────────────────── */

interface StructuredSectionProps {
  label: string
  state: 'empty' | 'partial' | 'full'
  fields: StructuredField[]
}

function StructuredSection({ label, state, fields }: StructuredSectionProps) {
  const [expanded, setExpanded] = useState(state !== 'empty')

  if (state === 'empty') {
    return (
      <div className="rounded-lg border border-border bg-bg-elevated px-4 py-3 flex items-center justify-between">
        <span className="text-sm text-text-tertiary">{label}</span>
        <span className="text-xs text-text-tertiary">No data</span>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-bg-elevated overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-bg-surface-hover"
      >
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="text-xs text-text-tertiary">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {fields.map((field) => {
            const values = Array.isArray(field.value) ? field.value : null
            if (values && values.length > 0) {
              return (
                <div key={field.label}>
                  <span className="text-xs text-text-tertiary">
                    {field.label}
                  </span>
                  <ul className="mt-1 space-y-1">
                    {values.map((v, i) => (
                      <li
                        key={i}
                        className="text-sm text-text-primary leading-relaxed"
                      >
                        {v}
                      </li>
                    ))}
                  </ul>
                  <Separator className="my-3" />
                </div>
              )
            }
            if (field.value != null && field.value !== '') {
              return (
                <div key={field.label}>
                  <span className="text-xs text-text-tertiary">
                    {field.label}
                  </span>
                  <p className="mt-1 text-sm text-text-primary leading-relaxed">
                    {String(field.value)}
                  </p>
                  <Separator className="my-3" />
                </div>
              )
            }
            return (
              <div key={field.label} className="flex items-center gap-2">
                <span className="text-xs text-text-tertiary">
                  {field.label}
                </span>
                <span className="text-xs text-text-tertiary italic">
                  Not available
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
